import { HttpError, useList, useOne } from "@refinedev/core";
import { useEffect, useRef, useState } from "react";
import { EventTicketsTable } from "./table";
import { EventTicketsCard } from "./card";
import Pagination from "components/pagination";
import Select from "components/select";
import { IEventTicketPhaseIdsItem, IEventTicketsIdsItem } from "interfaces";
import { isEmpty } from "lodash";

const ALL_OPTION = {value: "", label: "All"};

const TICKET_TYPES = [
  {label: "All", value: ""},
  {label: "Individual", value: "individual"},
  {label: "Group", value: "group_buy"},
  {label: "Change distance", value: "change_distance"},
  {label: "Transfer", value: "transfer_ticket"},
];

export const Tickets = ({currentFilterValues, isTrigger, type, currency}: {currentFilterValues: any, isTrigger: boolean, type: string | undefined, currency?: string}) => {
  const prevFilterKeyRef = useRef<string | null>(null);
  const [ticketFilters, setTicketFilters] = useState({
    ticket_phase: "",
    ticket_name: "",
    order_type: "",
    page: "1"
  });
  const {
    data,
    refetch,
    isLoading,
  } = useList<any>({
    resource: `dashboard/event/${currentFilterValues.event_id}/ticket`,
    pagination: {
      mode: "off"
    },
    meta: {
      resource: "data",
      filters: [
        { field: "ticket_phase", operator: "eq", value: ticketFilters.ticket_phase },
        { field: "ticket_name", operator: "eq", value: ticketFilters.ticket_name },
        { field: "from_date", operator: "eq", value: currentFilterValues.from_date },
        { field: "to_date", operator: "eq", value: currentFilterValues.to_date },
        { field: "order_type", operator: "eq", value: ticketFilters.order_type },
        { field: "ticket_platform", operator: "eq", value: currentFilterValues.ticket_platform },
        { field: "currency", operator: "eq", value: localStorage.getItem("currency") ?? "" },
        { field: "page", operator: "eq", value: ticketFilters.page },
        { field: "gateway_owner", operator: "eq", value: currentFilterValues.gateway_owner ?? "" },
        { field: "gateway_partner_code", operator: "eq", value: currentFilterValues.gateway_partner_code ?? "" },
        { field: "gateway_name", operator: "eq", value: currentFilterValues.gateway_name ?? "" },
      ],
    },
    queryOptions: {
      enabled: false
    } as any
  });

  const { data: eventTicketsIdsData, isLoading: isLoadingTickets } = useOne<IEventTicketsIdsItem[], HttpError>({
    resource: "dashboard/event",
    id: `${currentFilterValues?.event_id}/ticket-names`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(currentFilterValues.event_id  && isTrigger),
    } as any,
  });

  const { data: eventTicketPhasesIdsData, isLoading: isLoadingTicketPhases } = useOne<IEventTicketPhaseIdsItem[], HttpError>({
    resource: "dashboard/event",
    id: `${currentFilterValues?.event_id}/ticket-phases`,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(currentFilterValues.event_id  && isTrigger),
    } as any,
  });


  const eventTicketsIds = ((eventTicketsIdsData?.data ?? []).length > 0 ? [ALL_OPTION] : []).concat((eventTicketsIdsData?.data ?? []).map((ticket: IEventTicketsIdsItem) => (
    { value: ticket.product_name_en, label: ticket.product_name_en }
  )));

  const eventTicketPhasesIds = ((eventTicketPhasesIdsData?.data ?? []).length > 0 ? [ALL_OPTION] : []).concat((eventTicketPhasesIdsData?.data ?? []).map((phase: IEventTicketPhaseIdsItem) => (
    { value: phase.id, label: phase.name_en }
  )));

  const _handleFilterChange = (key: string, value: any) => {
    const updatedFilter = {
      [key]: value,
      ...(key !== "page" && {page: "1"})
    }
    setTicketFilters({
      ...ticketFilters,
      ...updatedFilter
    })
  }

  useEffect(() => {
    if (isTrigger) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketFilters, isTrigger]);

  useEffect(() => {
    const key = JSON.stringify(currentFilterValues);
    if (prevFilterKeyRef.current === null) {
      prevFilterKeyRef.current = key;
      return;
    }
    if (prevFilterKeyRef.current === key) return;
    prevFilterKeyRef.current = key;
    setTicketFilters({
      ticket_phase: "",
      ticket_name: "",
      order_type: "",
      page: "1"
    })
  }, [currentFilterValues]); // reset state on filter props change


  const tableQueryResult = data;

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-y-4 lg:flex-row gap-x-2 md:gap-x-3 mb-4">
        <div className="flex-1">
          <label htmlFor="ticket-name" className="block text-sm font-medium leading-6">
            Ticket Name
          </label>
          <div className="mt-2">
            <Select 
              key={`event-tickets__${currentFilterValues.event_id}`}
              options={eventTicketsIds}
              id="event-ticket"
              isLoading={isLoadingTickets}
              placeholder="Select Ticket"
              className="py-0.5 w-full sm:max-w-full !rounded-md"
              value={eventTicketsIds.find((selected: {label: string; value: string}) => selected.value === (ticketFilters.ticket_name)) || ""}
              onChange={(selected: {label: string; value: string}) => _handleFilterChange("ticket_name", selected.value)}      
            />
          </div>
        </div>
        {!isEmpty(eventTicketPhasesIds) ? (
          <div className="flex-1">
            <label htmlFor="ticket-phase" className="block text-sm font-medium leading-6">
              Ticket Phase
            </label>
            <div className="mt-2">
              <Select 
                key={`event-tickets-phase__${currentFilterValues.event_id}`}
                options={eventTicketPhasesIds}
                id="event-phase"
                isLoading={isLoadingTicketPhases}
                placeholder="Select Phase"
                className="py-0.5 w-full sm:max-w-full !rounded-md"
                value={eventTicketPhasesIds.find((selected: {label: string; value: string}) => selected.value === (ticketFilters.ticket_phase)) || ""}
                onChange={(selected: {label: string; value: string}) => _handleFilterChange("ticket_phase", selected.value)}      
              />
            </div>
          </div>
        ) : <></>}
        {type !== "attractions" ? (
          <div className="flex-1">
            <label htmlFor="ticket-type" className="block text-sm font-medium leading-6">
              Ticket Type
            </label>
            <div className="mt-2">
              <Select 
                key={`event-order-type__${currentFilterValues.event_id}`}
                options={TICKET_TYPES}
                id="order-type"
                placeholder="Select Type"
                className="py-0.5 w-full sm:max-w-full !rounded-md"
                value={TICKET_TYPES.find((selected: {label: string; value: string}) => selected.value === (ticketFilters.order_type)) || ""}
                onChange={(selected: {label: string; value: string}) => _handleFilterChange("order_type", selected.value)}      
              />
            </div>
          </div>
        ) : <></>}
      </div>
      <div className="sm:hidden">
        <EventTicketsCard 
          data={tableQueryResult?.data}
          pageSize={tableQueryResult?.pageSize}
          currentPage={tableQueryResult?.paging.current_page}
          isLoading={isLoading}
          currency={currency}
        />
      </div>
      <div className="hidden sm:block">
        <EventTicketsTable
          data={tableQueryResult?.data}
          pageSize={tableQueryResult?.pageSize}
          currentPage={tableQueryResult?.paging.current_page}
          isLoading={isLoading}
          currency={currency}
        />
      </div>
      {tableQueryResult?.paging  && tableQueryResult.paging.total_page > 1 ? (
        <div className="mt-6">
          <Pagination 
            hideTotal
            currentPage={tableQueryResult?.paging.current_page}
            totalPage={tableQueryResult?.paging.total_page}
            total={{
              totalItems: tableQueryResult?.paging.total_item || 0,
              total: tableQueryResult?.paging.total
            }}
            onChange={(page: number) => _handleFilterChange("page", page)}
          />
        </div>
      ) : <></>}
    </div>
  )
}